import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

export default function PendingOrdersBarChart({
  data = [],
  total = 0
}) {
  /* ================= SAFE DATA ================= */
  const safeData = Array.isArray(data) ? data : [];

  const labels = safeData.map(d => d.month);
  const isSinglePoint = labels.length === 1;
  const values = safeData.map(d => {
    const v = Number(d.total_orders ?? d.count);
    return Number.isFinite(v) ? v : 0;
  });

  const computedTotal = useMemo(
    () => values.reduce((s, v) => s + v, 0),
    [values]
  );

  const displayTotal = total || computedTotal;
  const isMobile = window.innerWidth < 640;

  /* ================= SCROLL WIDTH ================= */
  const pointWidth = isMobile ? 90 : 110; // spacing per month
  const chartWidth = isMobile
    ? Math.max(labels.length * pointWidth, window.innerWidth)
    : isSinglePoint ? 280 : "100%";

  /* ================= CHART DATA ================= */
  const chartData = {
    labels,
    datasets: [
      {
        label: "Pending Orders",
        data: values,
        borderColor: "#505ecb",
        backgroundColor: "rgba(80,94,203,0.15)",
        fill: true,
        tension: 0.35,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointHitRadius: 18,
        pointBackgroundColor: "#505ecb",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2
      }
    ]
  };

  /* ================= AXIS ================= */
  const maxValue = Math.max(...values, 0);
  const yAxisMax = maxValue > 0 ? Math.ceil(maxValue / 100) * 100 : 10;

  /* ================= OPTIONS ================= */
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false
    },
    events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],
    layout: {
      padding: {
        top: 20,
        bottom: 20
      }
    },
    plugins: {
      legend: { display: false },

      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#ffffff",
        titleColor: "#3f3f5f",
        bodyColor: "#3f3f5f",
        borderColor: "#E0E0FF",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: ctx => {
            const monthData = safeData[ctx.dataIndex] || {};
            const totalOrders = Number(monthData.total_orders ?? monthData.count ?? ctx.raw ?? 0);
            const partialOrders = Number(monthData.partial_orders ?? 0);
            const notDeliveredOrders = Number(monthData.not_delivered_orders ?? 0);

            return [
              `Total Orders: ${totalOrders.toLocaleString()} Mtr`,
              `Partially Processed: ${partialOrders.toLocaleString()} Mtr`,
              `Not Yet Processed: ${notDeliveredOrders.toLocaleString()} Mtr`
            ];
          }
        }
      },

      datalabels: {
        display: true,
        anchor: "end",
        align: "top",
        offset: ctx => (ctx.raw < 50 ? 10 : 0),
        color: "#505ecb",
        formatter: value => value.toLocaleString(),
        font: {
          size: isMobile ? 10 : 12,
          weight: "bold"
        },
        backgroundColor: "rgba(80,94,203,0.08)",
        borderRadius: 6,
        padding: 4,
        clamp: true,
        clip: false
      }
    },

    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,              // ✅ no overlap
          padding: 14,                  // ✅ push labels down
          maxRotation: isMobile ? 0 : 45,
          minRotation: isMobile ? 0 : 45,
          color: "#6b6b8a",
          font: { size: isMobile ? 10 : 13 }
        }
      },
      y: {
        beginAtZero: true,
        max: yAxisMax,
        ticks: {
          color: "#6b6b8a",
          font: { size: isMobile ? 11 : 13 }
        },
        grid: {
          color: "rgba(120,120,170,0.15)",
          drawBorder: false
        }
      }
    }
  };

  return (
    <div
      style={{
        position: "relative",
        padding: isMobile ? 16 : 28,
        borderRadius: 20,
        background:
          "radial-gradient(circle at top,#EDE9FF 0%,#F6F4FF 40%,#FFFFFF 80%)",
        boxShadow: "0 14px 30px rgba(90,111,227,0.15)"
      }}
    >
      {/* SCROLL HINT */}
      {isMobile && (
        <div
          style={{
            fontSize: 11,
            color: "#6b6b8a",
            textAlign: "right",
            marginBottom: 4
          }}
        >
          ← Swipe →
        </div>
      )}

      {/* SCROLLABLE CHART */}
      <div
        style={{
          height: 280,
          position: "relative",
          overflowX: isMobile ? "auto" : "hidden",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch"
        }}
      >
        <div style={{ width: chartWidth, height: "100%" }}>
          <Chart type="line" data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
}
